import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, API } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Loader2, Download, Search, X, ExternalLink, ArrowUpDown } from "lucide-react";

const STATUSES = ["New Intake","Consult Scheduled","Active Strategy",
  "Gathering Evidence","Ready to File","Filed and Waiting","Decision Received"];
const BRANCHES = ["Army","Navy","Air Force","Marine Corps","Coast Guard","Space Force","National Guard","Reserves"];
const GOALS = ["First rating","Increase to a specific percentage","Reach 100%","P&T status","Appeal a denial","Not sure yet"];

const COLS = [
  { k: "full_name", l: "Name" },
  { k: "state", l: "State" },
  { k: "branches", l: "Branch" },
  { k: "current_rating", l: "Rating" },
  { k: "primary_goal", l: "Goal" },
  { k: "status", l: "Status" },
  { k: "created_at", l: "Intake" },
];

export default function VeteransTable() {
  const { user } = useAuth();
  const [rows, setRows] = useState(null);
  const [f, setF] = useState({ status: "", branch: "", state: "", goal: "", min_rating: "", max_rating: "", search: "" });
  const [sort, setSort] = useState({ k: "created_at", dir: -1 });

  const load = async () => {
    const params = Object.entries(f).filter(([, v]) => v !== "" && v !== null)
      .map(([k, v]) => `${k}=${encodeURIComponent(v)}`).join("&");
    const { data } = await api.get(`/veterans${params ? "?" + params : ""}`);
    setRows(data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [f]);

  const clear = () => setF({ status: "", branch: "", state: "", goal: "", min_rating: "", max_rating: "", search: "" });

  const sorted = useMemo(() => {
    if (!rows) return [];
    return [...rows].sort((a, b) => {
      const av = a[sort.k], bv = b[sort.k];
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      if (typeof av === "number" && typeof bv === "number") return (av - bv) * sort.dir;
      return String(av).localeCompare(String(bv)) * sort.dir;
    });
  }, [rows, sort]);

  const setSortKey = (k) => setSort(s => s.k === k ? { k, dir: -s.dir } : { k, dir: 1 });

  if (!user || user.role !== "admin") return <div className="p-24 text-center">Admin only.</div>;

  return (
    <div className="mx-auto max-w-[1600px] px-5 lg:px-10 py-10">
      <div className="mb-8 flex items-end justify-between flex-wrap gap-4">
        <div>
          <Link to="/admin" className="text-sm text-primary/60 hover:text-primary font-semibold" data-testid="back-to-kanban">← Kanban view</Link>
          <div className="label-caps text-primary/70 mt-4">Admin, Veterans</div>
          <h1 className="font-display text-3xl lg:text-5xl mt-1">All veterans</h1>
        </div>
        <a href={`${API}/veterans/export.csv`} target="_blank" rel="noreferrer"
           className="inline-flex items-center gap-2 h-11 px-5 bg-accent text-accent-foreground font-semibold hover:bg-accent/90"
           data-testid="export-csv">
          <Download className="w-4 h-4" /> Export CSV
        </a>
      </div>

      {/* Filters */}
      <Card className="p-5 border-2 mb-6" data-testid="filters">
        <div className="grid md:grid-cols-3 lg:grid-cols-7 gap-3">
          <div className="lg:col-span-2">
            <label className="text-xs font-semibold uppercase tracking-widest text-primary/70">Search</label>
            <div className="mt-1 relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-primary/40" />
              <Input value={f.search} onChange={(e) => setF({ ...f, search: e.target.value })}
                placeholder="Name, email, condition, MOS" className="pl-9 h-11" data-testid="filter-search" />
            </div>
          </div>
          <FilterSelect label="Status" value={f.status} onChange={(v) => setF({ ...f, status: v })} options={STATUSES} testid="filter-status" />
          <FilterSelect label="Branch" value={f.branch} onChange={(v) => setF({ ...f, branch: v })} options={BRANCHES} testid="filter-branch" />
          <div>
            <label className="text-xs font-semibold uppercase tracking-widest text-primary/70">State</label>
            <Input value={f.state} onChange={(e) => setF({ ...f, state: e.target.value.toUpperCase().slice(0,2) })} placeholder="TX" className="mt-1 h-11" data-testid="filter-state" />
          </div>
          <FilterSelect label="Goal" value={f.goal} onChange={(v) => setF({ ...f, goal: v })} options={GOALS} testid="filter-goal" />
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-xs font-semibold uppercase tracking-widest text-primary/70">Min %</label>
              <Input type="number" min="0" max="100" value={f.min_rating} onChange={(e) => setF({ ...f, min_rating: e.target.value })} className="mt-1 h-11" data-testid="filter-min-rating" />
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-widest text-primary/70">Max %</label>
              <Input type="number" min="0" max="100" value={f.max_rating} onChange={(e) => setF({ ...f, max_rating: e.target.value })} className="mt-1 h-11" data-testid="filter-max-rating" />
            </div>
          </div>
        </div>
        <div className="mt-4 flex items-center justify-between">
          <div className="text-sm text-primary/70">{rows ? `${rows.length} result${rows.length === 1 ? "" : "s"}` : "…"}</div>
          <button onClick={clear} className="text-sm text-primary hover:underline flex items-center gap-1" data-testid="filter-clear">
            <X className="w-3.5 h-3.5" /> Clear filters
          </button>
        </div>
      </Card>

      {!rows ? (
        <div className="grid place-items-center h-64"><Loader2 className="w-6 h-6 animate-spin" /></div>
      ) : (
        <Card className="p-0 border-2 overflow-x-auto">
          <table className="min-w-[1000px] w-full text-sm" data-testid="veterans-table">
            <thead className="bg-secondary">
              <tr>
                {COLS.map((c) => (
                  <th key={c.k} className="text-left p-3 font-semibold border-r border-border last:border-r-0">
                    <button className="inline-flex items-center gap-1 hover:text-accent-foreground" onClick={() => setSortKey(c.k)} data-testid={`sort-${c.k}`}>
                      {c.l} <ArrowUpDown className="w-3 h-3 opacity-40" />
                    </button>
                  </th>
                ))}
                <th className="p-3"></th>
              </tr>
            </thead>
            <tbody>
              {sorted.map((v) => (
                <tr key={v.veteran_id} className="border-t border-border hover:bg-secondary/40" data-testid={`vet-row-${v.veteran_id}`}>
                  <td className="p-3 font-semibold">{v.full_name || `${v.first_name || ""} ${v.last_name || ""}`.trim() || "—"}<div className="text-xs text-primary/50 font-normal">{v.email}</div></td>
                  <td className="p-3">{v.state || "—"}</td>
                  <td className="p-3">{(v.branches || []).join(", ") || v.branch || "—"}</td>
                  <td className="p-3 font-mono">{v.current_rating ? `${v.current_rating}%` : "—"}</td>
                  <td className="p-3 text-xs">{v.primary_goal || v.goals || "—"}</td>
                  <td className="p-3"><span className="inline-block px-2 py-1 text-xs font-semibold bg-secondary border border-primary/20">{v.status}</span></td>
                  <td className="p-3 text-xs text-primary/60">{new Date(v.created_at).toLocaleDateString()}</td>
                  <td className="p-3"><Link to={`/admin/veteran/${v.veteran_id}`} className="text-primary hover:text-accent-foreground" data-testid={`open-${v.veteran_id}`}><ExternalLink className="w-4 h-4" /></Link></td>
                </tr>
              ))}
              {sorted.length === 0 && <tr><td colSpan={8} className="text-center py-10 text-primary/50">No veterans match these filters.</td></tr>}
            </tbody>
          </table>
        </Card>
      )}
    </div>
  );
}

function FilterSelect({ label, value, onChange, options, testid }) {
  return (
    <div>
      <label className="text-xs font-semibold uppercase tracking-widest text-primary/70">{label}</label>
      <Select value={value || "__all__"} onValueChange={(v) => onChange(v === "__all__" ? "" : v)}>
        <SelectTrigger className="mt-1 h-11" data-testid={testid}><SelectValue placeholder="Any" /></SelectTrigger>
        <SelectContent>
          <SelectItem value="__all__">Any</SelectItem>
          {options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );
}
